import type * as Api from '@ViewModels';
import type { Node } from '@xyflow/react';
import { NODE_TYPES } from '../types';
import type { BaseNodeData } from '../types';
import type { ChainTerminus } from '../hooks/useChainTermini';

interface BranchCaseLike {
	id?: string;
	automationTemplateId?: string;
}

function getNodeTemplateId(node: Node<BaseNodeData> | null | undefined): string | undefined {
	if (!node) {
		return undefined;
	}

	const nodeData = node.data as { automationTemplate?: Api.IAutomationTemplate };
	return nodeData.automationTemplate?.id;
}

function resolveTemplateIdFromBranchInfo(
	terminus: ChainTerminus,
	nodes: Node<BaseNodeData>[],
	rootTemplateId?: string
): string | undefined {
	if (!terminus.branchInfo) {
		return rootTemplateId;
	}

	const { parentSwitchId, caseIndex, caseId: branchCaseId } = terminus.branchInfo;

	const matchedBranchNode = nodes.find(node => {
		if (node.type !== NODE_TYPES.SEGMENT && node.type !== NODE_TYPES.BEHAVIOR_CASE) {
			return false;
		}

		const nodeData = node.data as {
			parentSwitchNodeId?: unknown;
			segmentIndex?: unknown;
			caseIndex?: unknown;
			caseId?: unknown;
			childTemplateId?: unknown;
		};

		if (nodeData.parentSwitchNodeId !== parentSwitchId) {
			return false;
		}

		if (branchCaseId) {
			return nodeData.caseId === branchCaseId;
		}

		if (node.type === NODE_TYPES.SEGMENT) {
			return nodeData.segmentIndex === caseIndex;
		}

		return nodeData.caseIndex === caseIndex;
	});

	const directChildTemplateId = (matchedBranchNode?.data as { childTemplateId?: unknown } | undefined)?.childTemplateId;
	if (typeof directChildTemplateId === 'string' && directChildTemplateId.length > 0) {
		return directChildTemplateId;
	}

	const switchNode = nodes.find(node => node.id === parentSwitchId);
	const switchStep = (switchNode?.data as { automationStep?: Api.ISwitchAutomationStep } | undefined)?.automationStep;
	if (switchStep?.cases) {
		const cases = switchStep.cases as BranchCaseLike[];
		const matchedCase = branchCaseId ? cases.find(candidate => candidate.id === branchCaseId) : cases[caseIndex];
		if (matchedCase?.automationTemplateId) {
			return matchedCase.automationTemplateId;
		}
	}

	return rootTemplateId;
}

export function resolveTargetTemplateIdForTerminus({
	terminus,
	nodes,
	rootTemplateId,
}: {
	terminus: ChainTerminus | null | undefined;
	nodes: Node<BaseNodeData>[];
	rootTemplateId?: string;
}): string | undefined {
	if (!terminus) {
		return rootTemplateId;
	}

	const ownTemplateId = getNodeTemplateId(terminus.node);
	if (ownTemplateId) {
		return ownTemplateId;
	}

	const childTemplateId = (terminus.node.data as { childTemplateId?: unknown }).childTemplateId;
	if (typeof childTemplateId === 'string' && childTemplateId.length > 0) {
		return childTemplateId;
	}

	return resolveTemplateIdFromBranchInfo(terminus, nodes, rootTemplateId);
}

export function buildTemplateForNode({
	rootTemplate,
	targetTemplateId,
	existingTemplate,
}: {
	rootTemplate?: Api.IAutomationTemplate;
	targetTemplateId?: string;
	existingTemplate?: Api.IAutomationTemplate;
}): Api.IAutomationTemplate | undefined {
	if (existingTemplate && (!targetTemplateId || existingTemplate.id === targetTemplateId)) {
		return existingTemplate;
	}

	if (!rootTemplate) {
		return existingTemplate;
	}

	if (targetTemplateId && rootTemplate.id !== targetTemplateId) {
		return { ...rootTemplate, id: targetTemplateId };
	}

	return rootTemplate;
}
